import { FormattedMessage, useIntl } from 'react-intl';

import type { ExpanderListItemBase } from '#components/shared/ExpanderListItemBase.ts';
import type { Documents } from '#domain/Document.ts';
import type { StopDetails } from '#domain/Stop.ts';
import type { Tour } from '#domain/Tour.ts';
import { DocumentGroup } from '#features/documents/ui/DocumentGroup.tsx';

import { TourStopDetails } from './TourStopDetails.tsx';

type TourExpanderItemsProps = {
    tour: Tour;
    documents?: Documents;
    selectedItemIndex: number | undefined;
    setSelectedItemIndex: (index: number | undefined) => void;
    setSelectedStop: (stop?: StopDetails) => void;
    setSelectedDocumentStop: (stop?: StopDetails) => void;
    showDocuments: boolean;
};

export const getTourExpanderItems = ({
    tour,
    documents,
    setSelectedItemIndex,
    selectedItemIndex,
    setSelectedStop,
    setSelectedDocumentStop,
    showDocuments,
}: TourExpanderItemsProps) => {
    const intl = useIntl();

    const tourDocuments =
        documents?.filter(doc => doc.associations?.some(ac => ac.type === 'tour' && ac.id === tour.id)) ?? [];

    const expanderItems: ExpanderListItemBase[] = [];

    const tourStopsItem: ExpanderListItemBase = {
        id: 'tour-stops',
        header: <FormattedMessage data-testid={'tour-stops-div'} id={'intl-msg:ordercom-cloud-frontend.tour.stops'} />,
        body: (
            <TourStopDetails
                handleLoadDeviation={setSelectedStop}
                openStopDocuments={setSelectedDocumentStop}
                tourDocuments={documents}
            />
        ),
    };

    const documentsItem: ExpanderListItemBase = {
        id: 'documents',
        header: (
            <span data-testid={'documents-div'}>
                {`${intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.documents.tour.header',
                })} ${tourDocuments.length ? `(${tourDocuments.length})` : ''}`}
            </span>
        ),
        body: (
            <DocumentGroup
                documents={tourDocuments}
                upload={{
                    associations: [{ type: 'tour', id: tour.id }],
                    target: { tourId: tour.id },
                    buttonTitle: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tour.documents.delivery-upload',
                    }),
                }}
            />
        ),
    };
    expanderItems.push(tourStopsItem);
    if (showDocuments) {
        expanderItems.push(documentsItem);
    }

    return expanderItems.map((item, index) => ({
        ...item,
        open: selectedItemIndex === index,
        onOpen: () => setSelectedItemIndex(index),
        onClose: () => setSelectedItemIndex(undefined),
        bodyClassName: 'padding-top-0',
    }));
};
